import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { MintExchangeAvailableCurrency } from '@flipster/apis/v1/earn/types';
import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useOverlayItem } from '@flipster/overlay';
import { Badge, Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { AssetPrecision, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';

import { useWalletModal } from '../WalletModal/hooks/useWalletModal';
import { mintBunja } from './state';

const DepositGuide = () => {
  const t = useTranslations();
  const { onClose } = useOverlayItem();
  const { openWalletModal } = useWalletModal();
  const { providerCurrencyAtom, collateralCurrenciesAtom } = useBunja(mintBunja);
  const providerCurrency = useAtomValue(providerCurrencyAtom);
  const collateralCurrencies = useAtomValue(collateralCurrenciesAtom);

  const { walletsAtom } = useBunja(walletsBunja);
  const wallets = useAtomValue(walletsAtom);

  const { currenciesKeyByCurrency } = useSnapshotSpec();

  const supportedCurrencies = useMemo(
    () =>
      collateralCurrencies.map((currencyInfo) => {
        const balance = Num(wallets?.[currencyInfo.currency]?.walletBalanceExclLocked ?? '0');

        // 0 < balance < 0.01 이면 2자리, 아니면 원래 자릿수
        const precision =
          balance.gt(0) && balance.lt(0.01)
            ? currenciesKeyByCurrency?.[currencyInfo.currency]?.fullDisplayPrecision ?? AssetPrecision
            : UsdtPrecision;

        return {
          ...currencyInfo,
          balance: balance.toFixed(precision),
        };
      }),
    [collateralCurrencies, wallets, currenciesKeyByCurrency],
  );

  const openDeposit = (currency: string) => {
    onClose();
    openWalletModal('deposit', { defaultCurrency: currency });
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Mint.r251008.Deposit_to_mint_quote_currency', { quoteCurrency: providerCurrency })} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          {supportedCurrencies.map((currencyInfo) => (
            <DepositCurrencyItem key={currencyInfo.currency} currencyInfo={currencyInfo} onClick={openDeposit} />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          label={t('Common.r251008.Not_now')}
          hierarchy='secondary'
          size='large'
          color='neutral'
          onClick={onClose}
        />
      </Modal.Footer>
    </>
  );
};

export default DepositGuide;

const DepositCurrencyItem = ({
  currencyInfo,
  onClick,
}: {
  currencyInfo: MintExchangeAvailableCurrency & { balance: string };
  onClick: (currency: string) => void;
}) => {
  const t = useTranslations();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { appliedAprPct } = getEarnSummaryOfCurrency(currencyInfo.currency, earnSummary);
  const appliedAprPctText = appliedAprPct.toString();
  const needToShowApr = appliedAprPct.gt(0);

  return (
    <div
      className={hstack({
        justify: 'space-between',
        p: 16,
        border: '1px solid',
        borderColor: 'border.default.level-1',
        rounded: 8,
      })}
    >
      <div className={stack({ gap: 8 })}>
        <div>
          <CurrencyDisplay
            currency={currencyInfo.currency}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={
              <div className={hstack({ gap: 8 })}>
                <CurrencyDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-4' }}
                />
                {needToShowApr && (
                  <Badge color='positive' type='outline'>
                    {t('Common.r251008.Apr_percent_apr', { apr: appliedAprPctText })}
                  </Badge>
                )}
              </div>
            }
          />
        </div>
        <div>
          <Formatted.Number
            value={currencyInfo.balance}
            suffix={currencyInfo.currency}
            toFixed
            css={{ textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-4' }}
          />
        </div>
        <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3', pt: 6 })}>
          {t('Mint.r251008.Min_amount_colon_amount_quote_currency', {
            amountQuoteCurrency: `${numberFormatter(currencyInfo.minAmount, { withComma: true })} ${currencyInfo.currency}`,
          })}
        </p>
      </div>
      <div>
        <Button
          label={t('Navigation.r250401.Deposit')}
          hierarchy='primary'
          color='accent'
          size='medium'
          onClick={() => onClick(currencyInfo.currency)}
        />
      </div>
    </div>
  );
};
